/**
 * Віртуалізація рядків сталої висоти для ядра списку (RecordTable; перший екран,
 * performance-budget.md, розд. 3). Замість `@tanstack/react-virtual` на першому екрані: рядок списку
 * завжди висоти `rowHeight` профілю можливостей, а загальний віртуалізатор з динамічним виміром
 * коштував першому екрану ≈ 10,5 КБ gzip (virtual-core 3.19). Адмінка (поля моделі, довідники)
 * лишається на TanStack Virtual — це її лінивий чанк.
 *
 * Поведінка — та сама, що давав RecordTable віртуалізатор TanStack:
 * - діапазон — видимі рядки + запас `overscan` з обох боків; рядок `keep` (фокус, LST-14) лишається
 *   в DOM, навіть коли за екраном (діапазон тоді з розривом);
 * - `endIndex` — останній ВИДИМИЙ рядок (без запасу): за ним список вирішує, чи тягнути наступну
 *   сторінку;
 * - висота області — лише з ResizeObserver (border-box), до першого колбека — `initialHeight`: так
 *   перший кадр малює рядки без синхронної розкладки (spike S4, ≈ 100 мс на еталоні);
 * - прокрутка до рядка `align: 'auto'` — як у TanStack: рядок нижче видимого — до низу області,
 *   вище — до верху, видимий — без прокрутки; межа — `[0, count × rowHeight − висота]`;
 * - новий контейнер стоїть на 0, тож на монтуванні прокручуємо лише до ненульового `initialOffset`
 *   (повернення до списку, UX-08): `scrollTo` теж змушує рахувати розкладку.
 * Перемальовуємо лише тоді, коли змінився діапазон, а не на кожну подію прокрутки; під час прокрутки —
 * синхронно (`flushSync`), щоб нові рядки з'являлись у тому самому кадрі, без порожнечі.
 */
import { type RefObject, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';

export interface RowWindowItem {
  index: number;
  key: string;
  /** Верх рядка від початку тіла таблиці, px. */
  start: number;
  /** Низ рядка, px. */
  end: number;
}

export interface RowRange {
  /** Перший видимий рядок. */
  startIndex: number;
  /** Останній видимий рядок (без запасу). */
  endIndex: number;
}

/** Видимий діапазон для прокрутки `offset` і висоти області `height`; `null` — рядків немає. */
export function visibleRange(
  count: number,
  rowHeight: number,
  offset: number,
  height: number,
): RowRange | null {
  if (count === 0 || rowHeight <= 0) return null;
  const last = count - 1;
  const startIndex = Math.min(last, Math.max(0, Math.floor(offset / rowHeight)));
  // Останній рядок, чий верх вище за низ області (як `calculateRange` у TanStack Virtual).
  const endIndex = Math.min(
    last,
    Math.max(startIndex, Math.ceil((offset + height) / rowHeight) - 1),
  );
  return { startIndex, endIndex };
}

/** Номери рядків у DOM: видимі ± запас і рядок `keep`, за зростанням. */
export function windowIndexes(
  range: RowRange | null,
  count: number,
  overscan: number,
  keep: number,
): number[] {
  if (!range) return [];
  const from = Math.max(0, range.startIndex - overscan);
  const to = Math.min(count - 1, range.endIndex + overscan);
  const out: number[] = [];
  for (let i = from; i <= to; i++) out.push(i);
  if (keep >= 0 && keep < count && (keep < from || keep > to)) {
    if (keep < from) out.unshift(keep);
    else out.push(keep);
  }
  return out;
}

/** Куди прокрутити, щоб рядок `index` став видимим (`align: 'auto'`); `null` — він уже видимий. */
export function offsetToReveal(
  index: number,
  count: number,
  rowHeight: number,
  offset: number,
  height: number,
): number | null {
  if (count === 0) return null;
  const i = Math.max(0, Math.min(index, count - 1));
  const start = i * rowHeight;
  const end = start + rowHeight;
  let to: number;
  if (end >= offset + height) to = end - height;
  else if (start <= offset) to = start;
  else return null;
  return Math.max(0, Math.min(to, count * rowHeight - height));
}

/**
 * Від чого залежить набір рядків у DOM: видимий діапазон і параметри вікна. Перемальовуємо, лише
 * коли це змінилось, а не на кожну подію прокрутки.
 */
const windowKey = (range: RowRange | null, count: number, overscan: number, keep: number) =>
  `${range?.startIndex ?? -1}:${range?.endIndex ?? -1}:${count}:${overscan}:${keep}`;

export interface RowWindowOptions {
  count: number;
  rowHeight: number;
  overscan: number;
  /** Висота області до першого виміру ResizeObserver, px. */
  initialHeight: number;
  /** Прокрутка на монтуванні контейнера (повернення до списку, UX-08), px. */
  initialOffset: number;
  /** Контейнер прокрутки; може з'явитись пізніше за перший рендер (порожній список → рядки). */
  scrollRef: RefObject<HTMLElement | null>;
  /** Рядок, який лишається в DOM поза видимими (фокус); −1 — немає. */
  keep: number;
  getItemKey: (index: number) => string;
}

export interface RowWindow {
  items: RowWindowItem[];
  /** Останній видимий рядок (без запасу); 0 — рядків немає. */
  endIndex: number;
  /** Поточна прокрутка й висота області, px (без читання DOM). */
  scrollOffset: () => number;
  viewportHeight: () => number;
  totalSize: number;
  scrollToIndex: (index: number) => void;
  scrollToOffset: (offset: number) => void;
}

export function useRowWindow({
  count,
  rowHeight,
  overscan,
  initialHeight,
  initialOffset,
  scrollRef,
  keep,
  getItemKey,
}: RowWindowOptions): RowWindow {
  const offset = useRef(initialOffset);
  const height = useRef(initialHeight);
  const [, setTick] = useState(0);
  // Параметри останнього рендеру — для обробників подій без перепідписки. `latest` і `rendered`
  // пишемо під час рендеру: відкинутий конкурентний рендер лише змусить наступну подію
  // перемалювати ще раз (ключ інший), рядків від цього не бракує.
  const latest = useRef({ count, rowHeight, overscan, keep });
  latest.current = { count, rowHeight, overscan, keep };
  const rendered = useRef('');

  const attached = useRef<HTMLElement | null>(null);
  const detach = useRef<() => void>(() => {});
  // Без залежностей: контейнер з'являється й зникає разом із рядками, перевіряємо на кожен коміт.
  useLayoutEffect(() => {
    const element = scrollRef.current;
    if (element === attached.current) return;
    detach.current();
    attached.current = element;
    detach.current = () => {};
    if (!element) return;
    const update = (sync: boolean) => {
      const p = latest.current;
      const r = visibleRange(p.count, p.rowHeight, offset.current, height.current);
      if (windowKey(r, p.count, p.overscan, p.keep) === rendered.current) return;
      if (sync) flushSync(() => setTick((n) => n + 1));
      else setTick((n) => n + 1);
    };
    if (offset.current !== 0) {
      // Контейнер (новий чи знову під'єднаний) може бути коротшим за збережену позицію: браузер
      // урізає `scrollTop`, а події `scroll` без зміни позиції немає. Звіряємо одразу — розкладку
      // вже порахував сам запис.
      element.scrollTop = offset.current;
      offset.current = element.scrollTop;
      update(false);
    }
    const onScroll = () => {
      offset.current = element.scrollTop;
      update(true);
    };
    element.addEventListener('scroll', onScroll, { passive: true });
    let observer: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(([entry]) => {
        const box = entry?.borderBoxSize?.[0];
        height.current = box ? Math.round(box.blockSize) : element.offsetHeight;
        update(false);
      });
      observer.observe(element, { box: 'border-box' });
    }
    detach.current = () => {
      element.removeEventListener('scroll', onScroll);
      observer?.disconnect();
    };
  });
  useLayoutEffect(
    () => () => {
      detach.current();
      attached.current = null;
    },
    [],
  );

  const range = visibleRange(count, rowHeight, offset.current, height.current);
  const indexes = windowIndexes(range, count, overscan, keep);
  rendered.current = windowKey(range, count, overscan, keep);

  // Прокрутку беремо лише з події `scroll`, як TanStack: браузер може урізати її до максимуму, а
  // без зміни позиції події немає — запис наперед лишив би діапазон не там, де людина.
  const scrollTo = (to: number) => scrollRef.current?.scrollTo({ top: to });
  // Стабільні функції: їх беруть залежності ефектів і `useCallback` списку.
  // biome-ignore lint/correctness/useExhaustiveDependencies: читають лише refs
  const actions = useMemo(
    () => ({
      scrollOffset: () => offset.current,
      viewportHeight: () => height.current,
      scrollToOffset: (to: number) => scrollTo(to),
      scrollToIndex: (index: number) => {
        const p = latest.current;
        const to = offsetToReveal(index, p.count, p.rowHeight, offset.current, height.current);
        if (to !== null) scrollTo(to);
      },
    }),
    [],
  );

  return {
    ...actions,
    items: indexes.map((index) => ({
      index,
      key: getItemKey(index),
      start: index * rowHeight,
      end: (index + 1) * rowHeight,
    })),
    endIndex: range?.endIndex ?? 0,
    totalSize: count * rowHeight,
  };
}
